<!-- AssignTeamDialog.vue -->
<template>
  <BaseDialog
    v-model="internalValue"
    @input="$emit('input', $event)"
    :title="$t('volunteers.mic_assign_team')"
    :cancelText="$t('cancel')"
    :confirmText="$t('confirm')"
    :loading="loading"
    :maxWidth="'500px'"
    :backgroundColor="'#f9efd5'"
    :confirmColor="'#1976d2'"
    :confirmDisabled="!selectedClub || !selectedTeam"
    @cancel="onCancel"
    @confirm="handleConfirm"
  >
    <!-- Volunteer Info Preview -->
    <v-container class="mb-3" style="background: #f5f5f5; border-radius: 6px;" v-if="volunteer">
      <v-layout row wrap>
        <v-flex xs12 sm6>
          <div class="label">{{ $t('params.name') }}:</div>
          <div class="value font-weight-bold">{{ volunteer.name || '-' }}</div>

          <div class="label">{{ $t('params.username') }}:</div>
          <div class="value font-weight-bold">{{ volunteer.username || '-' }}</div>
        </v-flex>
        <v-flex xs12 sm6>
          <div class="label">{{ $t('params.surname') }}:</div>
          <div class="value font-weight-bold">{{ volunteer.surname || '-' }}</div>

          <div class="label">{{ $t('params.gender') }}:</div>
          <div class="value font-weight-bold">
            {{ $t(`gender.${volunteer.gender}`) || '-' }}
          </div>
        </v-flex>
      </v-layout>
    </v-container>


    <v-form ref="form" v-model="formIsValid">
      <!-- Club Selection -->
      <v-autocomplete
        v-model="selectedClub"
        :items="clubs"
        item-text="name"
        item-value="id"
        :label="$t('assign_volunteer.select_club')"
        :loading="isLoading"
        :disabled="isLoading"
        dense
        outlined
        clearable
        :rules="[requiredRule]"
        @change="onClubChange"
      >
        <template v-slot:selection="{ item }">
          <v-avatar size="32" class="mr-2" v-if="item.shield && item.shield.length && item.shield !== 'null'">
            <img :src="item.shield" :alt="item.name" />
          </v-avatar>
          {{ item.name }}
        </template>

        <template v-slot:item="{ item }">
          <v-list-tile-avatar v-if="item.shield && item.shield.length && item.shield !== 'null'">
            <img :src="item.shield" :alt="item.name" />
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title v-html="item.name" />
          </v-list-tile-content>
        </template>
      </v-autocomplete>

      <!-- Team Selection -->
      <v-select
        v-if="teams.length"
        v-model="selectedTeam"
        :items="teams"
        item-value="value"
        item-text="text"
        :label="$t('competition.sanction.select.team')"
        dense
        outlined
        :rules="[requiredRule]"
        @change="onTeamChange"
      >
        <template v-slot:selection="{ item }">
          <v-avatar size="32" class="mr-2" v-if="item.shield && item.shield.length && item.shield !== 'null'">
            <img :src="item.shield" :alt="item.text" />
          </v-avatar>
          {{ item.text }}
        </template>

        <template v-slot:item="{ item }">
          <v-list-tile-avatar v-if="item.shield && item.shield.length && item.shield !== 'null'">
            <img :src="item.shield" :alt="item.text" />
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ item.text }}</v-list-tile-title>
          </v-list-tile-content>
        </template>
      </v-select>
    </v-form>
  </BaseDialog>
</template>

<script>
import BaseDialog from "@/components/ui/BaseDialog.vue";

export default {
  name: "AssignTeamDialog",
  components: { BaseDialog },
  props: {
    value: { type: Boolean, required: true },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      formIsValid: false,
      selectedClub: null,
      selectedTeam: null,
      isLoading: false,
    volunteer: null 
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    clubs() {
      const list = this.$store.getters["clubs/getClubList"]();
      return Object.values(list || {});
    },
    teams() {
      const club = this.clubs.find(c => c.id === this.selectedClub);
      if (!club || !club.teams) return [];

      return Object.values(club.teams).map(team => {
        let name = (!team.name || team.name === 'null') ? club.name : team.name;
        if (team.category) {
          name += ` (${team.category})`;
        }
        return {
          value: team.id,
          text: name,
          shield: team.shield
        };
      });
    }
  },
  watch: {
    internalValue(newVal) {
      if (newVal) {
        this.fetchClubs();
        this.fetchVolunteer();
      }
    }
  },
  methods: {
    resetState() {
      this.formIsValid = false;
      this.selectedClub = null;
      this.selectedTeam = null;
      this.isLoading = false;
      this.volunteer = null;
    },
    requiredRule(v) {
      return !!v || this.$t("required_field");
    },
    onCancel () {
      if (this.loading) return
      this.resetState();
      this.$emit('input', false) // close the dialog
    },
    async fetchClubs() {
      this.isLoading = true;
      const eventId = this.$store.getters["events/getCurrentEvent"]().id;
      await this.$store.dispatch("clubs/fetchByEventList", eventId);
      this.isLoading = false;
    },
    onClubChange() {
      this.selectedTeam = null; // reset team selection when club changes
    },
    onTeamChange() {
      // logic when team changes
      // console.log("Selected team:", this.selectedTeam);
    },
    handleConfirm() {
      this.$refs.form.validate();
      if (!this.formIsValid) return;

      this.$emit("assign", {
        clubId: this.selectedClub,
        teamId: this.selectedTeam
      });
    },
    async fetchVolunteer() {
      const id = this.$route && this.$route.params ? this.$route.params.id : undefined
      if (!id) return;
      this.volunteer = this.$store.getters["volunteers/get_by_id"](id);
    },
  }
};
</script>

<style scoped>
.label {
  font-size: 0.85rem;
  color: #555;
  margin-top: 8px;
}
.value {
  font-size: 1rem;
  margin-bottom: 12px;
}
</style>
